import { useEffect, useState } from "react";
import { bootSession } from "./session.ts";

export interface LessonProgress {
  position: number;
  duration: number;
  completed: boolean;
  updatedAt: number;
}
export type LearningProgress = Record<string, LessonProgress>;
export interface LearningProgressSnapshot { progress: LearningProgress; persistent: boolean }
type ProgressStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
const PREFIX = "piggo.learning.v1:";
export const LEARNING_PROGRESS_EVENT = "piggo-learning-progress";
const volatile = new Map<string, LearningProgress>();
const unavailable = new Set<string>();
const checkedStorage = new WeakSet<ProgressStorage>();

/** Scope uses an account name only, never a login token; anonymous use has a separate namespace. */
export function learningProgressScope(operator?: string | null): string {
  if (operator === undefined) {
    try { operator = bootSession().operator; } catch { operator = null; }
  }
  return operator ? `operator:${operator}` : "anonymous";
}
export const learningProgressKey = (scope: string) => PREFIX + encodeURIComponent(scope);
const finiteNonnegative = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value) && value >= 0;
const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === "object" && !Array.isArray(value);

/** Browser storage is untrusted. Discard malformed entries, including prototype keys. */
export function parseLearningProgress(raw: string | null): LearningProgress {
  const progress: LearningProgress = Object.create(null);
  try {
    const data: unknown = JSON.parse(raw ?? "{}");
    if (!object(data)) return progress;
    for (const [id, entry] of Object.entries(data)) {
      if (!id || ["__proto__", "constructor", "prototype"].includes(id) || !object(entry)) continue;
      const { position, duration, completed, updatedAt } = entry;
      if (!finiteNonnegative(position) || !finiteNonnegative(duration) || duration <= 0 ||
          !finiteNonnegative(updatedAt) || typeof completed !== "boolean") continue;
      progress[id] = { position: Math.min(position, duration), duration, completed, updatedAt };
    }
  } catch { /* A corrupt local record must not prevent playing a lesson. */ }
  return progress;
}

function browserStorage(): ProgressStorage { return window.localStorage; }
export function readLearningProgress(scope = learningProgressScope(), storage?: ProgressStorage): LearningProgressSnapshot {
  if (unavailable.has(scope)) return { progress: volatile.get(scope) ?? Object.create(null), persistent: false };
  try {
    const target = storage ?? browserStorage();
    const progress = parseLearningProgress(target.getItem(learningProgressKey(scope)));
    volatile.set(scope, progress);
    // Some browsers allow reads but reject writes (quota/private mode).
    if (!checkedStorage.has(target)) {
      const probe = learningProgressKey(scope) + ":probe";
      target.setItem(probe, "1");
      target.removeItem(probe);
      checkedStorage.add(target);
    }
    return { progress, persistent: true };
  } catch {
    unavailable.add(scope);
    return { progress: volatile.get(scope) ?? Object.create(null), persistent: false };
  }
}

export function saveLearningProgress(scope: string, lessonId: string,
  input: Pick<LessonProgress, "position" | "duration" | "completed">,
  storage?: ProgressStorage): LearningProgressSnapshot {
  const snapshot = readLearningProgress(scope, storage);
  if (!lessonId || ["__proto__", "constructor", "prototype"].includes(lessonId) ||
      !finiteNonnegative(input.position) || !finiteNonnegative(input.duration) || input.duration <= 0) return snapshot;
  const progress = { ...snapshot.progress, [lessonId]: {
    position: Math.min(input.position, input.duration), duration: input.duration,
    completed: snapshot.progress[lessonId]?.completed === true || input.completed === true,
    updatedAt: Date.now(),
  } };
  volatile.set(scope, progress);
  let persistent = snapshot.persistent;
  try { (storage ?? browserStorage()).setItem(learningProgressKey(scope), JSON.stringify(progress)); }
  catch { persistent = false; unavailable.add(scope); }
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(LEARNING_PROGRESS_EVENT, { detail: scope }));
  return { progress, persistent };
}

/** Completed lessons restart for review; unfinished lessons resume at a valid media position. */
export function resumeLearningPosition(progress: LessonProgress | undefined, duration: number): number {
  if (!progress || progress.completed || !finiteNonnegative(duration) || duration <= 0) return 0;
  return Math.min(progress.position, Math.max(0, duration - 1));
}

/** Same-tab player updates and cross-tab storage updates both refresh the course overview. */
export function useLearningProgress(operator?: string | null): LearningProgressSnapshot {
  const scope = learningProgressScope(operator);
  const [state, setState] = useState(() => ({ scope, ...readLearningProgress(scope) }));
  useEffect(() => {
    const refresh = () => setState({ scope, ...readLearningProgress(scope) });
    const onLocal = (event: Event) => { if ((event as CustomEvent<string>).detail === scope) refresh(); };
    const onStorage = (event: StorageEvent) => { if (event.key === null || event.key === learningProgressKey(scope)) refresh(); };
    window.addEventListener(LEARNING_PROGRESS_EVENT, onLocal);
    window.addEventListener("storage", onStorage);
    refresh();
    return () => {
      window.removeEventListener(LEARNING_PROGRESS_EVENT, onLocal);
      window.removeEventListener("storage", onStorage);
    };
  }, [scope]);
  return state.scope === scope ? { progress: state.progress, persistent: state.persistent } : readLearningProgress(scope);
}
